import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useSession } from "../lib/session";
import { getCompetition, myStanding, myRounds, myCompetitionState } from "../lib/api";
import { supabase } from "../lib/supabase";
import { PageHead, Loading, Notice } from "../components/ui";
import Announcements from "../components/Announcements";
import NextStep from "../components/NextStep";

/**
 * What's on now.
 *
 * The four things a member opens the app for: the card they owe, their
 * tee time, where they lie, and anything the organisers have posted.
 * Everything else is a tab away rather than a chain of pages down.
 */

const METRIC = { nett: "Nett", gross: "Gross", stableford: "Points" };
const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
};
const hhmm = (t) => (t ?? "").slice(0, 5);

export default function Today() {
  const { id } = useParams();
  const { profile, isOrganiser } = useSession();

  const [comp, setComp] = useState(null);
  const [standing, setStanding] = useState(null);
  const [state, setState] = useState(null);
  const [mine, setMine] = useState([]);
  const [tee, setTee] = useState(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const c = await getCompetition(id);
        const roundIds = (c?.rounds ?? []).map((r) => r.id);
        const [s, r, st, tt] = await Promise.all([
          myStanding(id, profile.id).catch(() => null),
          myRounds(id, profile.id).catch(() => []),
          myCompetitionState(id, profile.id).catch(() => null),
          roundIds.length
            ? supabase.from("v_my_tee_time").select("*").in("round_id", roundIds)
                .then(({ data }) => data ?? [])
            : Promise.resolve([]),
        ]);
        setComp(c); setStanding(s); setState(st);
        setMine(r.sort((a, b) => a.rounds.round_number - b.rounds.round_number));
        setTee(tt);
        setReady(true);
      } catch (e) { setError(e.message); setReady(true); }
    })();
  }, [id, profile?.id]);

  if (!ready) return <Loading />;
  if (!comp) return <Notice kind="error">That competition isn't available.</Notice>;

  const rounds = [...(comp.rounds ?? [])].sort((a, b) => a.round_number - b.round_number);
  const live = rounds.find((r) => ["open", "in_play", "scoring"].includes(r.status));
  const due = mine.find((m) => ["not_started", "in_progress"].includes(m.card_status)
                            && ["open", "in_play", "scoring"].includes(m.rounds?.status));
  /**
   * The next posted tee time, not the open round's.
   *
   * A draw goes up days before a round opens for scoring — that's the
   * whole point of posting it — so tying it to the live round meant the
   * tile said nothing exactly when people wanted it.
   */
  const posted = (tee ?? []).filter((t) => t.posted_at);
  const order = new Map(rounds.map((r) => [r.id, r.round_number]));
  const myTee = posted
    .filter((t) => {
      const card = mine.find((m) => m.rounds?.id === t.round_id);
      return !["submitted", "no_return"].includes(card?.card_status);
    })
    .sort((a, b) => (order.get(a.round_id) ?? 99) - (order.get(b.round_id) ?? 99))[0];
  const teeRound = myTee ? rounds.find((r) => r.id === myTee.round_id) : null;

  const posKey = comp.primary_metric === "stableford" ? "pos_points"
               : comp.primary_metric === "gross" ? "pos_gross" : "pos_nett";
  const totKey = comp.primary_metric === "stableford" ? "total_points"
               : comp.primary_metric === "gross" ? "total_gross" : "total_nett";

  return (
    <>
      <PageHead
        title={live ? live.name : "Nothing on today"}
        sub={live
          ? [live.courses?.name,
             live.played_on && new Date(live.played_on).toLocaleDateString(
               "en-GB", { weekday: "long", day: "numeric", month: "long" })]
            .filter(Boolean).join(" · ")
          : `${rounds.length} round${rounds.length === 1 ? "" : "s"} in this competition`} />

      <Notice kind="error">{error}</Notice>

      {due ? (
        <NextStep tone="do" to={`/cards/${due.id}`}
                  title={due.card_status === "in_progress"
                    ? "Carry on with your card" : "Enter your card"}
                  detail={[due.rounds?.name,
                           due.playing_handicap != null && `playing off ${due.playing_handicap}`]
                          .filter(Boolean).join(" · ")}
                  action={due.card_status === "in_progress" ? "Carry on" : "Enter"} />
      ) : state?.entered && live ? (
        <NextStep tone="done" title="Your card is in"
                  detail="Nothing outstanding for this round."
                  to={`/c/${id}/board`} action="Leaderboard" />
      ) : state?.entered ? (
        <NextStep tone="wait" title="Nothing to enter yet"
                  detail="Your card appears here once a round opens for scoring." />
      ) : (
        <NextStep tone="wait" title="You're not entered in this competition"
                  detail={isOrganiser ? "Add yourself under Rounds."
                                      : "Ask your organiser to add you."} />
      )}

      <div className="pair">
        <Link to={teeRound ? `/c/${id}/r/${teeRound.id}/tee-times`
                           : live ? `/c/${id}/r/${live.id}` : `/c/${id}/rounds`}
              className="tile">
          <span className="eyebrow">
            {teeRound && teeRound.id !== live?.id ? teeRound.name : "Your tee time"}
          </span>
          <b>{myTee ? hhmm(myTee.tee_at) : "—"}</b>
          <small>
            {myTee?.playing_with ? `with ${myTee.playing_with}`
              : myTee ? "on your own"
              : "no draw posted yet"}
          </small>
        </Link>

        <Link to={`/c/${id}/board`} className="tile">
          <span className="eyebrow">You lie</span>
          <b className={standing?.[posKey] === 1 ? "gold" : undefined}>
            {standing?.[posKey] ? ordinal(standing[posKey]) : "—"}
          </b>
          <small>
            {standing?.[totKey] != null
              ? `${standing[totKey]} ${(METRIC[comp.primary_metric] ?? "nett").toLowerCase()}`
              : "nothing played yet"}
          </small>
        </Link>
      </div>

      <Announcements limit={3} />
    </>
  );
}
